import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { StatisticsService } from '../../../core/services/api-services/statistics.service';
import { TransactionsService } from '../../../core/services/api-services/transactions.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { PaginatorComponent } from '../../../shared/components/paginator/paginator.component';
import { CompanyInvoiceSummary } from './models/company-invoice-summary';
import { CompanyPaymentDetail } from './models/company-payment-detail';
import { CompanyPaymentDetailChild } from './models/company-payment-detail-child';
import { CompanyPaymentSlip } from './models/company-payment-slip';

declare var bootstrap: any;

@Component({
  selector: 'app-payment-history',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent, BreadcrumbComponent],
  templateUrl: './payment-history.component.html',
  styleUrl: './payment-history.component.css'
})
export class PaymentHistoryComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  // ───────────────────────────────────────────────────────────────
  // ⬛ Component State and Data
  // ───────────────────────────────────────────────────────────────
  @ViewChild('paymentDetailsOffcanvasEl') offcanvasElementRef!: ElementRef;

  companies: Array<CompanyInvoiceSummary> = [];
  filteredCompanies: Array<CompanyInvoiceSummary> = [];
  pagedCompanies: Array<CompanyInvoiceSummary> = [];

  selectedCompany: CompanyInvoiceSummary | null = null;
  paymentSummary: Array<CompanyPaymentDetail> = [];
  paymentDetails: Array<CompanyPaymentDetailChild> = [];
  paymentSlips: Array<CompanyPaymentSlip> = [];
  pagedPaymentSummary: Array<CompanyPaymentDetail> = [];

  // Track expansion state for each summary row
  // Key format: "paymentType_invoiceContextId_salaryCycleId"
  expandedRows = new Map<string, boolean>();

  // Track expansion state for Level 2 invoice rows (keyed by invoiceId)
  expandedInvoiceRows = new Map<number, boolean>();

  isLoading = false;
  isLoadingDetails = false;
  searchText: string = '';

  // Filter parameters - Single Month
  selectedMonth: string = '';  // Format: "YYYY-MM"
  endMonth: string = '';    // Format: "YYYY-MM"
  
  months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  years: number[] = [];

  // Main table pagination
  companiesPage = 1;
  companiesPageSize = 5;
  companiesPageSizeOptions = [5, 10, 25, 50];

  // Details table pagination
  detailsPage = 1;
  detailsPageSize = 5;
  detailsPageSizeOptions = [5, 10, 20];

  dataSubscriber$!: Subscription;

  // ───────────────────────────────────────────────────────────────
  // ⬛ Constructor
  // ───────────────────────────────────────────────────────────────
  constructor(
    private toaster: ToastrService,
    private breadcrumbService: BreadcrumbService,
    private statisticsService: StatisticsService,
    private transactionsService: TransactionsService
  ) {
    this.dataSubscriber$ = new Subscription();
    
    // Initialize to current month
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    this.selectedMonth = `${year}-${month}`;
    this.endMonth = `${year}-${month}`;
    
    // Generate year options (current year and 5 years back)
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 6; i++) {
      this.years.push(currentYear - i);
    }
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Lifecycle Hook
  // ───────────────────────────────────────────────────────────────
  ngOnInit(): void {
    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Manage Payments', icon: 'bi bi-credit-card' },
      { label: 'Payment History', icon: 'bi bi-receipt' }
    ]);

    // Load data for current month/year
    this.loadCompanyInvoiceSummary();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Helper: Generate unique key for parent rows
  // ───────────────────────────────────────────────────────────────
  private generateRowKey(summary: CompanyPaymentDetail): string {
    return `${summary.paymentType}_${summary.invoiceContextId}_${summary.salaryCycleId}`;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Data Loading
  // ───────────────────────────────────────────────────────────────
  loadCompanyInvoiceSummary(): void {
    if (!this.selectedMonth) {
      return;
    }

    this.isLoading = true;

    // Parse selected month
    const [year, monthStr] = this.selectedMonth.split('-');
    const monthNum = parseInt(monthStr, 10);

    this.dataSubscriber$.add(
      this.statisticsService.getCompanyInvoiceSummary(
        monthNum, 
        parseInt(year, 10),
      )
        .subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.companies = response.data;
              this.filteredCompanies = [...this.companies];
              this.resetCompaniesPagination();
            } else {
              this.toaster.error(response.message || 'Failed to load company invoice summary', 'Error');
            }
            this.isLoading = false;
          },
          error: (error) => {
            this.toaster.error(error?.error?.message || error?.message || 'An error occurred while loading data', 'Error');
            this.isLoading = false;
          }
        })
    );
  }

  loadCompanyPaymentDetails(companyId: number | string): void {
    this.isLoadingDetails = true;
    this.paymentSummary = [];
    this.paymentDetails = [];
    this.paymentSlips = [];
    this.expandedRows.clear();
    this.expandedInvoiceRows.clear();

    const numericCompanyId = typeof companyId === 'string' ? parseInt(companyId, 10) : companyId;

    // Parse selected month
    const [year, monthStr] = this.selectedMonth.split('-');
    const monthNum = parseInt(monthStr, 10);

    this.dataSubscriber$.add(
      this.statisticsService.getCompanyPaymentDetails(
        numericCompanyId, 
        monthNum,
        parseInt(year, 10)
      )
        .subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.paymentSummary = response.data.summary;
              this.paymentDetails = response.data.details;
              this.paymentSlips = response.data.slips ?? [];
              this.resetDetailsPagination();
            } else {
              this.toaster.error(response.message || 'Failed to load payment details', 'Error');
            }
            this.isLoadingDetails = false;
          },
          error: (error) => {
            this.toaster.error(error?.error?.message || error?.message || 'An error occurred while loading payment details', 'Error');
            this.isLoadingDetails = false;
          }
        })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Filter Methods
  // ───────────────────────────────────────────────────────────────
  onMonthChange(): void {
    if (this.selectedMonth) {
      this.loadCompanyInvoiceSummary();
    }
  }

  onFilterReset(): void {
    // Reset to current month
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    this.selectedMonth = `${year}-${month}`;
    
    this.loadCompanyInvoiceSummary();
  }

  // Helper method to format month display
  formatMonthDisplay(monthString: string): string {
    if (!monthString) return '';
    const [year, month] = monthString.split('-');
    const monthNum = parseInt(month, 10);
    const monthName = this.months.find(m => m.value === monthNum)?.label || month;
    return `${monthName} ${year}`;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Search Functionality
  // ───────────────────────────────────────────────────────────────
  performSearch(): void {
    const search = this.searchText.trim().toLowerCase();
    if (!search) {
      this.filteredCompanies = [...this.companies];
      this.resetCompaniesPagination();
      return;
    }

    this.filteredCompanies = this.companies.filter(company =>
      Object.values(company).some(val =>
        String(val).toLowerCase().includes(search)
      )
    );
    this.resetCompaniesPagination();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Offcanvas Management
  // ───────────────────────────────────────────────────────────────
  private offcanvasInstance: any | null = null;

  openPaymentDetails(company: CompanyInvoiceSummary): void {
    this.selectedCompany = company;
    
    // Load payment details for this company
    this.loadCompanyPaymentDetails(company.companyId);

    const offcanvasEl = this.offcanvasElementRef?.nativeElement;
    if (offcanvasEl) {
      if (!this.offcanvasInstance) {
        this.offcanvasInstance = new bootstrap.Offcanvas(offcanvasEl, {
          backdrop: 'false',
          keyboard: false
        });
      }
      this.offcanvasInstance.show();
    }
  }

  closeOffcanvas(): void {
    if (this.offcanvasInstance) {
      this.offcanvasInstance.hide();
    }
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Row Expansion for Invoices
  // ───────────────────────────────────────────────────────────────
  toggleRowExpansion(summary: CompanyPaymentDetail): void {
    const key = this.generateRowKey(summary);
    this.expandedRows.set(key, !this.isRowExpanded(summary));
  }

  isRowExpanded(summary: CompanyPaymentDetail): boolean {
    const key = this.generateRowKey(summary);
    return this.expandedRows.get(key) || false;
  }

  hasMultipleInvoices(summary: CompanyPaymentDetail): boolean {
    return summary.invoiceCount > 1;
  }

  // Returns one row per distinct invoice for a given summary group (Level 2 rows).
  getChildInvoices(summary: CompanyPaymentDetail): CompanyPaymentDetailChild[] {
    return this.paymentDetails.filter(
      detail =>
        detail.paymentType === summary.paymentType &&
        detail.invoiceContextId === summary.invoiceContextId &&
        detail.salaryCycleId === summary.salaryCycleId
    );
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Level 2 → Level 3 Expansion (EWW_TOTAL payslip rows)
  // ───────────────────────────────────────────────────────────────
  isEwwTotal(paymentType: string): boolean {
    const type = paymentType?.toUpperCase();
    return type === 'EWW_TOTAL' || type === 'EWW';
  }

  toggleInvoiceExpansion(invoiceId: number): void {
    this.expandedInvoiceRows.set(invoiceId, !this.isInvoiceExpanded(invoiceId));
  }

  isInvoiceExpanded(invoiceId: number): boolean {
    return this.expandedInvoiceRows.get(invoiceId) || false;
  }

  // Returns all payslip rows for a given invoice (Level 3 rows).
  // Primary key: invoiceId — the SP stores the correct invoiceId on every slip.
  getSlipsForInvoice(invoice: CompanyPaymentDetailChild): CompanyPaymentSlip[] {
    return this.paymentSlips.filter(
      s => s.invoiceId === invoice.invoiceId && s.paymentType === invoice.paymentType
    );
  }

  // Returns the latest payment date on the invoice row (from RS2 directly).
  getInvoiceLatestPaymentDate(invoiceId: number): string | null {
    return this.paymentDetails.find(d => d.invoiceId === invoiceId)?.paymentDate ?? null;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Payslip Download
  // ───────────────────────────────────────────────────────────────
  downloadSlip(slip: CompanyPaymentSlip): void {
    if (!slip.paySlipUrl) return;
    this.dataSubscriber$.add(
      this.transactionsService.downlaodPayrollPaymentPayslip(slip.paySlipUrl)
        .subscribe({
          next: (blob: Blob) => {
            const ext = this.getSlipExt(slip.paySlipUrl!, blob.type);
            const objUrl = URL.createObjectURL(blob);
            const dateStr = slip.paymentDate
              ? new Date(slip.paymentDate).toISOString().split('T')[0]
              : 'unknown';
            const fileName = `payment-slip-v${slip.payslipVersion ?? 1}-${dateStr}.${ext}`;
            this.triggerSlipDownload(objUrl, fileName);
            setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
          },
          error: (err) => {
            this.toaster.error(
              err?.error?.message || 'Failed to download payment slip',
              'Error'
            );
          }
        })
    );
  }

  private triggerSlipDownload(objectUrl: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getSlipExt(url: string, mimeType: string): string {
    // Prefer extension from the URL (reliable even when MIME is octet-stream)
    const urlExt = url.split('?')[0].split('.').pop()?.toLowerCase();
    if (urlExt && ['pdf', 'jpg', 'jpeg', 'png'].includes(urlExt)) return urlExt;
    // Fall back to MIME type
    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpg';
    if (mimeType.includes('png')) return 'png';
    return 'pdf'; // safe default — slips are almost always PDF
  }

  getSlipVersionLabel(slip: CompanyPaymentSlip): string {
    const method = slip.paymentMethod?.toLowerCase() ?? '';
    if (method.includes('rollover')) {
      return method.includes('repay') ? 'V2 (Rollover)' : 'V1 (Rollover)';
    }
    return `v${slip.payslipVersion ?? 1}`;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Pagination Management
  // ───────────────────────────────────────────────────────────────
  private resetCompaniesPagination(): void {
    this.companiesPage = 1;
    this.updateCompaniesPage();
  }

  private updateCompaniesPage(): void {
    if (this.companiesPage < 1) this.companiesPage = 1;
    const start = (this.companiesPage - 1) * this.companiesPageSize;
    this.pagedCompanies = this.filteredCompanies.slice(start, start + this.companiesPageSize);
  }

  onCompanyPageChange(paginatedData: CompanyInvoiceSummary[]): void {
    this.pagedCompanies = paginatedData;
  }

  private resetDetailsPagination(): void {
    this.detailsPage = 1;
    this.updateDetailsPage();
  }

  private updateDetailsPage(): void {
    if (!this.paymentSummary) return;
    if (this.detailsPage < 1) this.detailsPage = 1;
    const start = (this.detailsPage - 1) * this.detailsPageSize;
    this.pagedPaymentSummary = this.paymentSummary.slice(start, start + this.detailsPageSize);
  }

  onDetailsPageChange(paginatedData: CompanyPaymentDetail[]): void {
    this.pagedPaymentSummary = paginatedData;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Payment Type Display Helper
  // ───────────────────────────────────────────────────────────────
  getPaymentTypeLabel(type: string): string {
    switch (type.toUpperCase()) {
      case 'EWW':
      case 'EWW_TOTAL':
        return 'EWW';
      case 'ROLLOVER':
      case 'ROLLOVER_IMMEDIATE':
      case 'ROLLOVER-IMMEDIATE-PAYMENT':
        return 'Rollover - Immediate';
      case 'ROLLOVER-REPAYMENT':
        return 'Rollover - Repayment';
      default:
        return type;
    }
  }

  getPaymentTypeBadgeClass(type: string): string {
    switch (type?.toUpperCase()) {
      case 'EWW':
      case 'EWW_TOTAL':
        return 'ph-badge--eww';
      case 'ROLLOVER':
      case 'ROLLOVER_IMMEDIATE':
      case 'ROLLOVER-IMMEDIATE-PAYMENT':
        return 'ph-badge--rollimm';
      case 'ROLLOVER-REPAYMENT':
        return 'ph-badge--rollrep';
      default:
        return 'ph-badge--default';
    }
  }

  getSlipMethodBadgeClass(slip: CompanyPaymentSlip): string {
    const method = slip.paymentMethod?.toLowerCase() ?? '';
    if (method === 'bank payment') return 'ph-badge--bank';
    if (method.includes('rollover') && method.includes('immediate')) return 'ph-badge--rollimm';
    if (method.includes('rollover') && method.includes('repay')) return 'ph-badge--rollrep';
    if (method.includes('immediate')) return 'ph-badge--immediate';
    if (method.includes('repay')) return 'ph-badge--repayment';
    return 'ph-badge--default';
  }

  getVersionBadgeClass(slip: CompanyPaymentSlip): string {
    const method = slip.paymentMethod?.toLowerCase() ?? '';
    const v = slip.payslipVersion ?? 1;
    if (method.includes('rollover') && method.includes('repay')) return 'v-badge--r2';
    if (method.includes('rollover')) return 'v-badge--r1';
    if (v === 1) return 'v-badge--1';
    if (v === 2) return 'v-badge--2';
    return 'v-badge--3';
  }

  getDueDaysBadgeClass(days: number): string {
    if (days > 7) return 'num-badge num-badge--overdue';
    if (days > 0) return 'num-badge num-badge--warn';
    return 'num-badge';
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Status Badge Helpers
  // ───────────────────────────────────────────────────────────────
  getBalanceStatusClass(balance: number): string {
    if (balance === 0) return 'text-success';
    if (balance > 0) return 'text-danger';
    return 'text-muted';
  }

  getDueStatusClass(datesOfDue: number): string {
    if (datesOfDue === 0) return 'text-muted';
    if (datesOfDue <= 7) return 'text-warning';
    return 'text-danger';
  }

  getMonthLabel(monthValue: number): string {
    const month = this.months.find(m => m.value === monthValue);
    return month ? month.label : '';
  }

  getInvoiceStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'PAID':         return 'bg-success';
      case 'ISSUED':       return 'bg-warning text-dark';
      case 'PARTIALLYPAID':return 'bg-warning text-dark';
      case 'UNPAID':       return 'bg-danger';
      case 'REJECTED':     return 'bg-danger';
      case 'PENDING':      return 'bg-info text-dark';
      default:             return 'bg-secondary';
    }
  }

  getInvoiceStatusBadgeClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'PAID':         return 'ph-status--paid';
      case 'ISSUED':       return 'ph-status--issued';
      case 'PARTIALLYPAID':return 'ph-status--partial';
      case 'UNPAID':       return 'ph-status--unpaid';
      case 'REJECTED':     return 'ph-status--rejected';
      case 'PENDING':      return 'ph-status--pending';
      default:             return 'ph-status--default';
    }
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Cleanup
  // ───────────────────────────────────────────────────────────────
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSubscriber$.unsubscribe();
  }
}